import { useEffect, useRef, useState } from "react";
import { cacheGet, cacheLoad, cacheSet } from "@frontend/lib/data-cache";
import { useRefresh } from "@frontend/lib/refresh";

type AsyncState<T> = {
  status: "loading" | "error" | "ready";
  data: T | null;
  error: Error | null;
};

type UseAsyncOptions = {
  /**
   * When set, results survive navigations. Returning with the same deps and
   * refresh generation paints instantly and skips a network round-trip.
   */
  cacheKey?: string | null;
};

/**
 * Load data on mount and whenever `refresh()` bumps the SPA generation
 * (or when `deps` change).
 *
 * Soft refreshes always keep the previous payload painted — never flash a
 * skeleton. Null/failed results never overwrite a good payload on soft refresh.
 */
export function useAsync<T>(
  loader: () => Promise<T>,
  deps: unknown[] = [],
  options: UseAsyncOptions = {},
): AsyncState<T> & { reload: () => void } {
  const { generation } = useRefresh();
  const [tick, setTick] = useState(0);
  const cacheKey = options.cacheKey ?? null;
  const depsKey = JSON.stringify(deps);
  const stamp = `${generation}:${depsKey}:${tick}`;

  const [state, setState] = useState<AsyncState<T>>(() => {
    if (cacheKey) {
      const cached = cacheGet<T>(cacheKey);
      if (cached && cached.data != null) {
        return { status: "ready", data: cached.data, error: null };
      }
    }
    return { status: "loading", data: null, error: null };
  });

  const prevDepsKey = useRef(depsKey);
  const prevCacheKey = useRef(cacheKey);
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    let cancelled = false;
    const depsChanged = prevDepsKey.current !== depsKey;
    const keyChanged = prevCacheKey.current !== cacheKey;
    prevDepsKey.current = depsKey;
    prevCacheKey.current = cacheKey;

    const cached = cacheKey ? cacheGet<T>(cacheKey) : undefined;
    const cachedData = cached?.data ?? null;

    // Exact hit for this generation/deps — back-navigation, no refetch.
    if (cached && cached.stamp === stamp && cachedData != null) {
      setState({ status: "ready", data: cachedData, error: null });
      return;
    }

    if (cachedData != null) {
      setState({ status: "ready", data: cachedData, error: null });
    } else if (depsChanged || keyChanged) {
      setState({ status: "loading", data: null, error: null });
    }
    // Soft refresh with no cache: keep painting existing state (do not clear).

    const run = cacheKey ? cacheLoad(cacheKey, loader) : loader();

    run
      .then((data) => {
        if (cancelled) return;
        // Soft refresh that resolves to null/empty denial must not wipe a good room.
        if (data == null && stateRef.current.data != null && !depsChanged && !keyChanged) {
          return;
        }
        if (cacheKey && data != null) cacheSet(cacheKey, data, stamp);
        setState({ status: "ready", data, error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState((prev) => ({
          status: prev.data != null ? "ready" : "error",
          data: cachedData ?? prev.data,
          error: error instanceof Error ? error : new Error(String(error)),
        }));
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generation, tick, depsKey, cacheKey]);

  return { ...state, reload: () => setTick((n) => n + 1) };
}
